import React, { useEffect, useState } from "react";
import BreadCrumbs from "../../components/common/BreadCrumbs";
import Loader from "../../components/loader/Loading";
import { useOutletContext } from "react-router-dom";
import { getReq } from "../../apis/api";
import { CSVLink } from "react-csv";
import { useSelector } from "react-redux";
import PriceListTable from "../../components/DataTable/PriceListTable";
import { useSnackbar } from 'notistack';
import { money } from "../../utils/format";
import { StockPill } from "../../components/common/StatusPill";

const PriceList = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [tab, setTab] = useState("items"); // active tab
  const [itemData, setItemData] = useState([]);
  const [subitemData, setSubitemData] = useState([]);
  const [partData, setPartData] = useState([]);
  const [productData, setProductData] = useState([]); // P115
  const { permissions } = useOutletContext();
  const { roleName } = useSelector((state) => state.role);
  const { enqueueSnackbar } = useSnackbar();

  const reportingpermission =
    permissions?.[roleName]?.modules?.["Reporting"]?.pages?.["Reporting"]
      ?.sections?.["Price List"];

  const fetchProductReport = async () => {
    try {
      setIsLoading(true);
      const response = await getReq("/priceList");

      if (response.status === 200) {
        setItemData(response.items || []);
        setSubitemData(response.sub_items || []);
        setPartData(response.parts || []);
        setProductData(response.products || []);
      }
    } catch (error) {
      console.error("Error", error);
      enqueueSnackbar("Error in Fetching Price List", { variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchProductReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loads the price list once on mount
  }, []);

  // SGEW 18 Oct: each stock figure is the shared pill - red at or below the record's minimum, green above it (as the
  // Items page: an item counted by quantity on its Quantity, one sold by weight on its weight; parts have no minimum, so
  // only an empty stock is red)
  const isKg = (row) => String(row.type || "").toLowerCase() === "kilogram";
  const itemColumns = [
    { name: "#", selector: (_, index) => index + 1, sortable: false, width: "64px", },
    { name: "ID", selector: (row) => row.item_code || "N/A", sortable: true },
    { name: "Name", selector: (row) => row.item_name || "N/A", sortable: true },
    {
      name: "Quantity",
      selector: (row) => Number(row.actual_qty || 0),
      cell: (row) => (isKg(row) ? row.actual_qty || 0 : <StockPill stock={row.actual_qty} min={row.minimum_stock_alert} />),
      sortable: true,
    },
    { name: "Sell Price", selector: (row) => row.sell_price || 0, format: (row) => money(row.sell_price), sortable: true },
    {
      name: "Actual Weight",
      selector: (row) => Number(row.actual_weight_kg || 0),
      cell: (row) =>
        isKg(row) ? <StockPill stock={row.actual_weight_kg} min={row.minimum_stock_alert}>{row.actual_weight_kg || 0}</StockPill> : row.actual_weight_kg || 0,
      sortable: true,
    },
  ];

  const subitemColumns = [
    { name: "#", selector: (_, index) => index + 1, sortable: false, width: "64px", },
    { name: "ID", selector: (row) => row.subitem_code || "N/A", sortable: true },
    { name: "Name", selector: (row) => row.subitem_name || "N/A", sortable: true },
    {
      name: "Quantity",
      selector: (row) => Number(row.quantity || 0),
      cell: (row) => <StockPill stock={row.quantity} min={row.min_stock_alert} />,
      sortable: true,
    },
    { name: "Sell Price", selector: (row) => row.sell_price || 0, format: (row) => money(row.sell_price), sortable: true },
    { name: "Weight", selector: (row) => row.total_weight_kg || 0, sortable: true },
  ];

  const partColumns = [
    { name: "#", selector: (_, index) => index + 1, sortable: false, width: "64px", },
    { name: "ID", selector: (row) => row.parts_sku || "N/A", sortable: true },
    { name: "Name", selector: (row) => row.name || "N/A", sortable: true },
    { name: "Quantity", selector: (row) => Number(row.stock || 0), cell: (row) => <StockPill stock={row.stock} />, sortable: true },
    { name: "Sell Price", selector: (row) => row.sell_price || 0, format: (row) => money(row.sell_price), sortable: true },
    { name: "Weight", selector: (row) => row.weight || 0, sortable: true },
  ];

  // P115 (plan row 101): name, current stock, sell price, weight. The sell price of a product is what an order bills
  // it at (sgew_price, OrderTotals::RATE) - the server sends it as sell_price.
  const productColumns = [
    { name: "#", selector: (_, index) => index + 1, sortable: false, width: "64px", },
    { name: "ID", selector: (row) => row.product_sku || "N/A", sortable: true },
    { name: "Name", selector: (row) => row.product_name || "N/A", sortable: true },
    {
      name: "Current Stock",
      selector: (row) => Number(row.stock || 0),
      cell: (row) => <StockPill stock={row.stock} min={row.min_stock} />,
      sortable: true,
    },
    { name: "Sell Price", selector: (row) => row.sell_price || 0, format: (row) => money(row.sell_price), sortable: true },
    { name: "Weight", selector: (row) => row.weight || 0, sortable: true },
  ];

  const getColumns = () => {
    if (tab === "subitems") return subitemColumns;
    if (tab === "parts") return partColumns;
    if (tab === "products") return productColumns;
    return itemColumns;
  };

  const getData = () => {
    if (tab === "subitems") return subitemData;
    if (tab === "parts") return partData;
    if (tab === "products") return productData;
    return itemData;
  };

  // the search box looks in each tab's own code and name fields
  const FILTERS = {
    items: [{ name: "Code", selector: "item_code" }, { name: "Name", selector: "item_name" }],
    subitems: [{ name: "Code", selector: "subitem_code" }, { name: "Name", selector: "subitem_name" }],
    parts: [{ name: "Code", selector: "parts_sku" }, { name: "Name", selector: "name" }],
    products: [{ name: "Code", selector: "product_sku" }, { name: "Name", selector: "product_name" }],
  };

  const getCSVHeaders = () => {
    if (tab === "subitems") {
      return [
        { label: "#", key: "index" },
        { label: "ID", key: "subitem_code" },
        { label: "Name", key: "subitem_name" },
        { label: "Quantity", key: "quantity" },
        { label: "Sell Price", key: "sell_price" },
        { label: "Weight", key: "total_weight_kg" },
      ];
    } else if (tab === "products") {
      return [
        { label: "#", key: "index" },
        { label: "ID", key: "product_sku" },
        { label: "Name", key: "product_name" },
        { label: "Current Stock", key: "stock" },
        { label: "Sell Price", key: "sell_price" },
        { label: "Weight", key: "weight" },
      ];
    } else if (tab === "parts") {
      return [
        { label: "#", key: "index" },
        { label: "ID", key: "parts_sku" },
        { label: "Name", key: "name" },
        { label: "Quantity", key: "stock" },
        { label: "Sell Price", key: "sell_price" },
        { label: "Weight", key: "weight" },
      ];
    } else {
      return [
        { label: "#", key: "index" },
        { label: "ID", key: "item_code" },
        { label: "Name", key: "item_name" },
        { label: "Quantity", key: "actual_qty" },
        { label: "Sell Price", key: "sell_price" },
        { label: "Actual Weight", key: "actual_weight_kg" },
      ];
    }
  };

  const getCSVData = () => {
    return getData().map((row, index) => ({
      index: index + 1,
      ...row,
    }));
  };

  return (
    <div className="w-full h-full">
      <div className="flex justify-between items-center bg-white rounded-[0.42vw] w-full py-[1vw] md:py-[0.5vw] my-[2vw] md:my-[1vw] px-[4vw] md:px-[2vw] ">
        <BreadCrumbs title={"Home / Dashboard / Reporting / Price List"} />
        <CSVLink
          data={getCSVData()}
          headers={getCSVHeaders()}
          filename={`${tab}_Price_List.csv`}
          className="sgew-btn sgew-btn-primary"
        >
          Export
        </CSVLink>
      </div>

      {/* Tabs (wrap on a phone: four of them since P115) */}
      <div className="flex flex-wrap gap-2 px-[4vw] mb-3 md:mb-[1vw]">
        {["items", "subitems", "parts", "products"].map((key) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`sgew-tab capitalize ${tab === key ? "is-active" : ""}`}
          >
            {key}
          </button>
        ))}
      </div>

      <main className="px-[4vw]">
        {isLoading ? (
          <Loader />
        ) : reportingpermission?.read ? (
          <PriceListTable
            key={tab}
            columns={getColumns()}
            data={getData()}
            filterColumns={FILTERS[tab]}
            onItemAdded={fetchProductReport}
          />
        ) : (
          <p className="text-gray-500 py-10 text-center font-bold">
            You do not have permission to view this data.
          </p>
        )}
      </main>
    </div>
  );
};

export default PriceList;
